'use client';

import { RiskLevel } from '@prisma/client';
import { capitalCase } from 'change-case';
import { Fragment, useMemo, useState } from 'react';
import Button from '~/components/Button';
import Skeleton from '~/components/Loading/Skeleton';
import Tooltip from '~/components/Tooltip';
import { Input } from '~/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { cn } from '~/lib/utils';
import { api, type RouterOutputs } from '~/utils/api';
import enumToOptions from '~/utils/enumToOptions';

type Policy =
  RouterOutputs['policyEngine']['getAllDocumentEvaluationTypes']['policyGroups'][0]['policies'][0];

export type RiskThresholdItem = {
  policyId: string;
  riskLevel: RiskLevel;
  count: number;
};

const RISK_DOT: Record<RiskLevel, string> = {
  LOW: 'bg-emerald-500',
  MEDIUM: 'bg-amber-500',
  HIGH: 'bg-red-500',
  AUTO: 'bg-sky-500',
};

const RISK_OPTIONS = enumToOptions(RiskLevel);

const MIN_FINDINGS_TOOLTIP =
  'Task is created when unresolved findings for this policy reach this count at the selected severity.';

type RiskThresholdProps = {
  readOnly: boolean;
  riskThresholds: RiskThresholdItem[];
  setRiskThreshold: (partial: Partial<RiskThresholdItem>, policy: Policy) => void;
  /** Apply severity + count to every policy in the current evaluation type. */
  onApplyToAll?: (partial: Pick<RiskThresholdItem, 'riskLevel' | 'count'>) => void;
  docEvalTypeId: string | undefined | null;
  /** Show evaluation type name above the card. Default false. */
  showEvalTypeName?: boolean;
};

function SeveritySelect({
  value,
  onValueChange,
  disabled,
  ariaLabel,
  className,
}: {
  value: RiskLevel;
  onValueChange: (value: RiskLevel) => void;
  disabled?: boolean;
  ariaLabel: string;
  className?: string;
}) {
  return (
    <Select
      value={value}
      onValueChange={(next) => onValueChange(next as RiskLevel)}
      disabled={disabled}
    >
      <SelectTrigger aria-label={ariaLabel} className={cn('h-8 w-full font-normal', className)}>
        <SelectValue placeholder={capitalCase(value)} />
      </SelectTrigger>
      <SelectContent position="popper">
        {RISK_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            <span className="flex items-center gap-2">
              <span
                className={cn('size-1.5 shrink-0 rounded-full', RISK_DOT[option.value])}
                aria-hidden
              />
              {option.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function RiskThreshold(props: RiskThresholdProps) {
  const { data: allDocumentEvaluationTypes, isLoading } =
    api.policyEngine.getAllDocumentEvaluationTypes.useQuery();
  const [bulkRiskLevel, setBulkRiskLevel] = useState<RiskLevel>(RiskLevel.LOW);
  const [bulkCount, setBulkCount] = useState(0);

  const evalType = allDocumentEvaluationTypes?.documentEvaluationTypes.find(
    (i) => i.id === props.docEvalTypeId,
  );

  const groups = useMemo(() => {
    if (!evalType || !allDocumentEvaluationTypes) return [];
    return evalType.documentEvaluationTypePolicyGroup
      .map((i) => {
        const policyGroup = allDocumentEvaluationTypes.policyGroups.find(
          (g) => g.id === i.policyGroupId,
        );
        if (!policyGroup) return null;
        return policyGroup;
      })
      .filter(Boolean) as NonNullable<(typeof allDocumentEvaluationTypes.policyGroups)[number]>[];
  }, [evalType, allDocumentEvaluationTypes]);

  const allPolicies = useMemo(() => groups.flatMap((g) => g.policies), [groups]);

  if (isLoading) {
    return (
      <div className="border-border bg-card space-y-3 rounded-xl border p-3 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <Skeleton className="h-8 w-full" />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  if (!evalType) return null;

  const resolveThreshold = (policy: Policy): RiskThresholdItem => {
    const existing = props.riskThresholds.find((r) => r.policyId === policy.id);
    return (
      existing || {
        policyId: policy.id,
        riskLevel: RiskLevel.LOW,
        count: 0,
      }
    );
  };

  const handleApplyToAll = () => {
    if (props.readOnly) return;
    if (props.onApplyToAll) {
      props.onApplyToAll({ riskLevel: bulkRiskLevel, count: bulkCount });
      return;
    }
    allPolicies.forEach((policy) => {
      props.setRiskThreshold({ riskLevel: bulkRiskLevel, count: bulkCount }, policy);
    });
  };

  return (
    <div className="w-full min-w-0 space-y-2">
      {props.showEvalTypeName ? (
        <p className="text-foreground text-sm font-medium tracking-tight">{evalType.name}</p>
      ) : null}

      <div className="border-border bg-card w-full min-w-0 overflow-hidden rounded-xl border shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <table className="w-full table-fixed border-collapse text-left">
          <colgroup>
            <col />
            <col className="w-[9rem]" />
            <col className="w-[8rem]" />
            <col className="w-[4.75rem]" />
          </colgroup>
          <thead>
            <tr className="text-muted-foreground text-[13px]">
              <th className="px-3 pt-2.5 pb-2 font-medium">Policy</th>
              <th className="px-1.5 pt-2.5 pb-2 font-medium">Severity</th>
              <th className="px-1.5 pt-2.5 pb-2 font-medium whitespace-nowrap">
                <Tooltip
                  content={MIN_FINDINGS_TOOLTIP}
                  popoverProps={{ disablePortal: true }}
                  iconProps={{ className: 'ml-1 size-3' }}
                >
                  Min findings
                </Tooltip>
              </th>
              <th className="px-3 pt-2.5 pb-2" aria-hidden />
            </tr>
          </thead>
          <tbody>
            {!props.readOnly && (
              <tr className="border-border/60 bg-muted/40 border-t">
                <td className="px-3 py-2 align-middle">
                  <span className="text-muted-foreground text-[11px] font-medium tracking-[0.08em] uppercase">
                    Apply to all
                  </span>
                </td>
                <td className="px-1.5 py-2 align-middle">
                  <SeveritySelect
                    value={bulkRiskLevel}
                    onValueChange={setBulkRiskLevel}
                    ariaLabel="Bulk severity"
                  />
                </td>
                <td className="px-1.5 py-2 align-middle">
                  <Input
                    type="number"
                    min={0}
                    value={bulkCount}
                    onChange={(e) => setBulkCount(Number(e.target.value) || 0)}
                    aria-label="Bulk min findings"
                    className="h-8 w-full px-2 text-right tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                </td>
                <td className="px-2 py-2 align-middle">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    label="Apply"
                    className="h-8 w-full px-2"
                    onClick={handleApplyToAll}
                    disabled={allPolicies.length === 0}
                  />
                </td>
              </tr>
            )}
            {groups.map((group) => (
              <Fragment key={group.id}>
                <tr className="border-border/50 border-t">
                  <td colSpan={4} className="px-3 pt-3 pb-1.5">
                    <span className="text-muted-foreground text-[11px] font-medium tracking-[0.08em] uppercase">
                      {group.name}
                    </span>
                  </td>
                </tr>
                {group.policies.map((policy) => {
                  const threshold = resolveThreshold(policy);
                  return (
                    <tr
                      key={policy.id}
                      className="border-border/50 hover:bg-muted/50 border-t transition-colors"
                    >
                      <td className="overflow-hidden px-3 py-2 align-middle">
                        <span className="text-foreground block truncate text-sm font-medium">
                          {policy.name}
                        </span>
                      </td>
                      <td className="px-1.5 py-2 align-middle">
                        <SeveritySelect
                          value={threshold.riskLevel}
                          disabled={props.readOnly}
                          ariaLabel={`Severity for ${policy.name}`}
                          onValueChange={(riskLevel) =>
                            props.setRiskThreshold({ riskLevel }, policy)
                          }
                        />
                      </td>
                      <td className="px-1.5 py-2 align-middle">
                        <Input
                          type="number"
                          min={0}
                          value={threshold.count}
                          readOnly={props.readOnly}
                          disabled={props.readOnly}
                          aria-label={`Min findings for ${policy.name}`}
                          onChange={(e) =>
                            props.setRiskThreshold({ count: Number(e.target.value) || 0 }, policy)
                          }
                          className="h-8 w-full px-2 text-right tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        />
                      </td>
                      <td className="px-2 py-2" />
                    </tr>
                  );
                })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
